// Epic 10 Showcase — the owner-facing hooks behind the Showcase pane in the
// Collection tab. Three concerns:
//   * useShowcase()               — GET the owner's arranged showcase.
//   * useSetShowcaseMembership()  — add/remove an item (publish toggle).
//   * useReorderShowcase()        — persist a drag-arranged order.
//
// The pane reuses the PUBLIC wire shapes (PublicCollectionItem) the server
// serves for both the pane and the public page, so the owner's pane and the
// stranger's `/u` page render from the identical, money-free DTOs.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type { PublicCollectionItem } from "../public-collection/publicCollectionTypes";
import type { PublishTargetKind } from "./vaultTypes";

export interface ShowcaseResponse {
  data: { items: PublicCollectionItem[] };
}

/** The showcase-item kind on the wire ("certificate" | "sealed_dossier"). */
export type ShowcaseItemKind = PublicCollectionItem["kind"];

/** The publish endpoint keys on "share" vs "sealed_dossier"; a showcase
 *  certificate maps to the share endpoint, keyed on its share_id. */
export function publishKindFor(kind: ShowcaseItemKind): PublishTargetKind {
  return kind === "certificate" ? "share" : "sealed_dossier";
}

/** The stable id of a showcase item (share_id for a certificate, else the
 *  sealed_dossier_id) — also the publish endpoint's path id. */
export function showcaseItemId(item: PublicCollectionItem): string {
  return item.kind === "certificate" ? item.share_id : item.sealed_dossier_id;
}

async function fetchShowcase(): Promise<ShowcaseResponse> {
  const response = await fetch("/api/v1/vault/showcase", {
    method: "GET",
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Failed to load showcase (HTTP ${response.status})`);
  }
  return (await response.json()) as ShowcaseResponse;
}

function endpointFor(kind: ShowcaseItemKind, id: string): string {
  return kind === "certificate"
    ? `/api/v1/vault/shares/${id}/publish`
    : `/api/v1/vault/sealed-dossiers/${id}/publish`;
}

async function postPublish(
  kind: ShowcaseItemKind,
  id: string,
  state: boolean,
): Promise<void> {
  const response = await fetch(endpointFor(kind, id), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ state }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Showcase update failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
}

async function postReorder(
  items: { kind: ShowcaseItemKind; id: string }[],
): Promise<ShowcaseResponse> {
  const response = await fetch("/api/v1/vault/showcase/reorder", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ items }),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(`Reorder failed (HTTP ${response.status})`);
  }
  return (await response.json()) as ShowcaseResponse;
}

/** The owner's arranged showcase (the pane). Query key ["showcase"]. */
export function useShowcase() {
  return useQuery({
    queryKey: ["showcase"],
    queryFn: fetchShowcase,
    staleTime: 10_000,
  });
}

/** Add an item to (state=true) or remove it from (state=false) the showcase.
 *  This is the same publish toggle the detail page uses; here it doubles as
 *  the pane's "right-click → Showcase" / "Remove" and drag-to-add action. */
export function useSetShowcaseMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      id,
      state,
    }: {
      kind: ShowcaseItemKind;
      id: string;
      state: boolean;
    }) => postPublish(kind, id, state),
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ["showcase"] });
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
      if (vars?.kind === "certificate") {
        queryClient.invalidateQueries({ queryKey: ["share-detail", vars.id] });
      }
    },
  });
}

/** Persist the owner's drag-arranged order. Reconciles the ["showcase"] cache
 *  against the server's re-read on success. */
export function useReorderShowcase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: { kind: ShowcaseItemKind; id: string }[]) =>
      postReorder(items),
    onSuccess: (data) => {
      queryClient.setQueryData(["showcase"], data);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["showcase"] });
      queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    },
  });
}
